import { useState } from 'react';
import type { Registratie } from '@/types/domain';
import { exporteerNaarCsv, exporteerNaarExcel } from '@/services/exportService';
import {
  exporteerFotosAlsZip,
  exporteerGemeenteCsv,
  exporteerGemeenteExcel,
  exporteerGemeenteGeoJson,
} from '@/services/gemeenteExport';
import { useApp } from '@/state/AppContext';
import { Knop } from '@/components/ui/Knop';
import { DownloadIcoon } from '@/components/layout/Iconen';
import { getalNL } from '@/utils/format';
import './dashboard.css';

interface ExportPaneelProps {
  /** De registraties zoals ze nu gefilterd op het scherm staan. */
  registraties: Registratie[];
  bijMelding: (soort: 'info' | 'succes' | 'fout', tekst: string) => void;
}

/**
 * Exporteren, in twee blokken.
 *
 * De scheiding tussen intern en gemeente is met opzet zichtbaar: in de interne
 * export staan namen en telefoonnummers van bewoners, in die voor de gemeente
 * niet. Dat verschil moet je zien op het moment dat je klikt, niet achteraf
 * ontdekken.
 */
export function ExportPaneel({ registraties, bijMelding }: ExportPaneelProps): JSX.Element {
  const { alleenLezen } = useApp();
  const [fotosBezig, setFotosBezig] = useState(false);
  const [voortgang, setVoortgang] = useState<string | null>(null);

  const aantalFotos = registraties.reduce((som, r) => som + r.fotos.length, 0);

  const controleer = (): boolean => {
    if (registraties.length === 0) {
      bijMelding('info', 'Er is niets te exporteren met deze filters.');
      return false;
    }
    return true;
  };

  const exporteer = (fn: (r: Registratie[]) => void, wat: string) => {
    if (!controleer()) return;
    fn(registraties);
    bijMelding('succes', `${registraties.length} registratie(s) geëxporteerd naar ${wat}.`);
  };

  const haalFotos = async () => {
    if (!controleer()) return;
    if (aantalFotos === 0) {
      bijMelding('info', 'Bij deze selectie zitten geen foto’s.');
      return;
    }

    setFotosBezig(true);
    try {
      const resultaat = await exporteerFotosAlsZip(registraties, (gedaan, totaal) => {
        setVoortgang(`${gedaan} van ${totaal}`);
      });

      if (resultaat.mislukt > 0) {
        bijMelding(
          'fout',
          `${resultaat.aantal} foto’s gedownload, ${resultaat.mislukt} niet gelukt.`
        );
      } else {
        bijMelding('succes', `${resultaat.aantal} foto’s gedownload.`);
      }
    } catch (fout) {
      bijMelding(
        'fout',
        fout instanceof Error ? `Downloaden mislukte: ${fout.message}` : 'Downloaden mislukte.'
      );
    } finally {
      setFotosBezig(false);
      setVoortgang(null);
    }
  };

  return (
    <section className="export">
      <h2>Exporteren</h2>

      {/*
        Zeggen wát er meegaat, vóór het downloaden.
        Zonder dit merk je pas in Excel dat je met een filter aan hebt
        geëxporteerd en de helft mist.
      */}
      <p className="export__uitleg">
        <strong>{getalNL(registraties.length)}</strong>{' '}
        {registraties.length === 1 ? 'registratie' : 'registraties'} gaan mee
        {aantalFotos > 0 && (
          <>
            , met {getalNL(aantalFotos)} foto{aantalFotos === 1 ? '' : "'s"}
          </>
        )}
        . Dit volgt de filters hieronder.
      </p>

      <div className="export__blokken">
        {!alleenLezen && (
        <div className="export__blok">
          <div className="export__kop">
            <strong>Intern gebruik</strong>
            <span className="export__waarschuwing">bevat persoonsgegevens</span>
          </div>
          <p className="export__toelichting">
            Alle velden, inclusief naam, telefoon en e-mail van de bewoner. Alleen voor het
            projectteam.
          </p>
          <div className="export__knoppen">
            <Knop
              variant="secundair"
              compact
              onClick={() => exporteer(exporteerNaarExcel, 'Excel')}
            >
              Excel
            </Knop>
            <Knop variant="secundair" compact onClick={() => exporteer(exporteerNaarCsv, 'CSV')}>
              CSV
            </Knop>
          </div>
        </div>
        )}

        <div className="export__blok export__blok--gemeente">
          <div className="export__kop">
            <strong>Voor de gemeente</strong>
            <span className="export__veilig">zonder persoonsgegevens</span>
          </div>
          <p className="export__toelichting">
            Adressen, adviezen, kubieke meters en voortgang. Naam, telefoon en e-mail van
            bewoners zitten er niet in.
          </p>
          <div className="export__knoppen">
            <Knop
              variant="primair"
              compact
              icoon={<DownloadIcoon />}
              onClick={() => exporteer(exporteerGemeenteExcel, 'Excel')}
            >
              Excel
            </Knop>
            <Knop
              variant="secundair"
              compact
              onClick={() => exporteer(exporteerGemeenteCsv, 'CSV')}
            >
              CSV
            </Knop>
            <Knop
              variant="secundair"
              compact
              onClick={() => exporteer(exporteerGemeenteGeoJson, 'GeoJSON')}
            >
              GeoJSON
            </Knop>
            <Knop
              variant="secundair"
              compact
              bezig={fotosBezig}
              onClick={() => void haalFotos()}
            >
              {voortgang ? `Foto’s ${voortgang}` : `Foto’s (${aantalFotos})`}
            </Knop>
          </div>
          <p className="export__toelichting">
            GeoJSON opent rechtstreeks in QGIS. Foto’s komen als één zipbestand, met per adres
            een map en daarin een map per soort foto.
          </p>
        </div>
      </div>
    </section>
  );
}
